import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { landlordAPI, getUploadUrl } from '../../../lib/api';
import { formatCurrency, formatDate, getInitials } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Separator } from '../../ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { toast } from 'sonner';
import {
  Mail, Phone, Loader2, Trash2, UserPlus, Send, Pencil, CreditCard, MapPin,
  Briefcase, Heart, Cake, FileText, XCircle, CheckCircle, Upload, Download,
  Camera, Link2, EyeOff, LogOut
} from 'lucide-react';
import { TenantNoteDialog } from './TenantNoteDialog';

export const getStatusBadge = (type) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    pending_lease: 'bg-orange-100 text-orange-700',
    pending: 'bg-amber-100 text-amber-700',
    invited: 'bg-blue-100 text-blue-700',
    available: 'bg-cyan-100 text-cyan-700',
    former: 'bg-gray-100 text-gray-600',
    manual: 'bg-purple-100 text-purple-700'
  };
  return map[type] || 'bg-gray-100 text-gray-600';
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDate(iso);
};

export const TenantDetailPanel = ({
  open, onOpenChange, tenant, selectedType,
  isManager, can, basePath,
  getPropertyName, getUnitName,
  onTenantUpdated, onDataChanged,
  onEdit, onEmail, onInvite, onPayment,
  onOffboard, onRemove,
  onVerify, onReject, verifying, rejecting
}) => {
  const navigate = useNavigate();
  const [tenantDocuments, setTenantDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [resendingLease, setResendingLease] = useState(false);

  const getTenantId = () => tenant?.user_id || tenant?.pending_tenant_id || '';
  const getTenantEmail = () => tenant?.email || tenant?.tenant_email || '';
  const getTenantName = () => tenant?.name || tenant?.tenant_name || '';

  const getAvatarUrl = (t) => {
    if (!t?.avatar) return null;
    if (t.avatar.startsWith('http')) return t.avatar;
    return getUploadUrl(t.avatar);
  };

  const loadTenantDocuments = async (tenantId) => {
    if (!tenantId) { setTenantDocuments([]); return; }
    try {
      const res = await landlordAPI.listTenantDocuments(tenantId);
      setTenantDocuments(res.data || []);
    } catch { setTenantDocuments([]); }
  };

  useEffect(() => {
    if (open && tenant) loadTenantDocuments(getTenantId());
  }, [open, tenant?.user_id, tenant?.pending_tenant_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mostRecentLeaseDoc = tenantDocuments
    .filter(d => d.source === 'lease_acknowledgement' || d.source === 'lease_export')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await landlordAPI.uploadTenantPhoto(getTenantId(), formData);
      onTenantUpdated({ ...tenant, avatar: res.data.avatar_url });
      toast.success('Photo uploaded');
      onDataChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large (max 25 MB)');
      return;
    }
    const tid = getTenantId();
    if (!tid) { toast.error('Select a tenant first'); return; }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await landlordAPI.uploadTenantDocument(tid, fd);
      toast.success('Document uploaded');
      loadTenantDocuments(tid);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleEditNote = async (doc) => {
    try {
      const res = await landlordAPI.getTenantDocument(doc.doc_id);
      setEditingDoc(res.data);
      setNoteDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load note');
    }
  };

  const handleDownloadDocument = async (doc) => {
    if (doc.type === 'file' && doc.s3_key) {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/s3/${doc.s3_key}`;
      window.open(url, '_blank');
      return;
    }
    if (doc.type === 'note') {
      try {
        const res = await landlordAPI.getTenantDocument(doc.doc_id);
        const blob = new Blob([res.data.content || ''], { type: 'text/plain;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${(doc.name || 'note').replace(/[^\w. -]/g, '_')}.txt`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch {
        toast.error('Failed to download note');
      }
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    try {
      await landlordAPI.deleteTenantDocument(doc.doc_id);
      toast.success('Document deleted');
      loadTenantDocuments(getTenantId());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleResendLease = async () => {
    const tid = getTenantId();
    if (!tid) return;
    setResendingLease(true);
    try {
      const res = await landlordAPI.resendLeaseToTenant(tid);
      toast.success(`Resent "${res.data.doc_name}" to tenant`);
      loadTenantDocuments(tid);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend lease');
    } finally {
      setResendingLease(false);
    }
  };

  const handleGetRegistrationLink = async () => {
    try {
      const res = await landlordAPI.getRegistrationLink();
      await navigator.clipboard.writeText(res.data.registration_link);
      toast.success('Registration link copied to clipboard!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get registration link');
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="tenant-panel">
          {tenant && (
            <div className="flex flex-col h-full">
              {/* Panel Header with Photo */}
              <div className="bg-gradient-to-b from-[#2F4F4F] to-[#3a5f5f] p-6 text-white">
                <SheetHeader>
                  <div className="flex items-start gap-4">
                    <div className="relative group">
                      <Avatar className="h-20 w-20 border-2 border-white/30">
                        {getAvatarUrl(tenant) ? (
                          <AvatarImage src={getAvatarUrl(tenant)} alt={getTenantName()} />
                        ) : null}
                        <AvatarFallback className="bg-white/20 text-white text-xl">{getInitials(getTenantName())}</AvatarFallback>
                      </Avatar>
                      {selectedType !== 'invited' && (
                        <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                          {uploadingPhoto ? <Loader2 className="w-5 h-5 animate-spin text-white" /> : <Camera className="w-5 h-5 text-white" />}
                          <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="upload-photo-input" />
                        </label>
                      )}
                    </div>
                    <div className="flex-1">
                      <SheetTitle className="text-white text-xl">{getTenantName()}</SheetTitle>
                      <Badge className={`mt-2 ${getStatusBadge(selectedType)}`}>{tenant._status}</Badge>
                      <div className="mt-2 space-y-1 text-sm text-white/70">
                        {getTenantEmail() && (
                          <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{getTenantEmail()}</p>
                        )}
                        {(tenant.phone || tenant.tenant_phone) && (
                          <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{tenant.phone || tenant.tenant_phone}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </SheetHeader>
              </div>

              {/* Quick Actions Bar */}
              <div className="grid grid-cols-4 gap-1 p-3 bg-muted/50 border-b">
                {(!isManager || can('invite_tenants')) && (
                  <Button variant="ghost" size="sm" className="flex-col h-auto py-2 text-xs" onClick={onEdit} data-testid="panel-edit-btn">
                    <Pencil className="w-4 h-4 mb-1" /> Edit
                  </Button>
                )}
                {(!isManager || can('send_messages')) && (
                  getTenantEmail() ? (
                    <Button variant="ghost" size="sm" className="flex-col h-auto py-2 text-xs" onClick={onEmail} data-testid="panel-email-btn">
                      <Mail className="w-4 h-4 mb-1" /> Email
                    </Button>
                  ) : (
                    (!isManager || can('invite_tenants')) && (
                      <Button variant="ghost" size="sm" className="flex-col h-auto py-2 text-xs" onClick={() => onInvite('')} data-testid="panel-invite-btn">
                        <Send className="w-4 h-4 mb-1" /> Invite
                      </Button>
                    )
                  )
                )}
                {(!isManager || can('verify_payments')) && (
                  <Button variant="ghost" size="sm" className="flex-col h-auto py-2 text-xs" onClick={onPayment} data-testid="panel-payment-btn">
                    <CreditCard className="w-4 h-4 mb-1" /> Payment
                  </Button>
                )}
                {(!isManager || can('create_leases')) && (
                  <Button variant="ghost" size="sm" className="flex-col h-auto py-2 text-xs" onClick={() => navigate(`${basePath}/leases?newLease=true&tenant=${getTenantId()}`)} data-testid="panel-lease-btn">
                    <FileText className="w-4 h-4 mb-1" /> Lease
                  </Button>
                )}
              </div>

              {/* Pending Tenant Verification Section */}
              {selectedType === 'pending' && (!isManager || can('verify_tenants')) && (
                <div className="p-4 bg-amber-50 dark:bg-amber-950 border-b">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-3">This tenant is awaiting your approval</p>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={onVerify}
                      disabled={verifying || rejecting}
                      data-testid="verify-tenant-btn"
                    >
                      {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={onReject}
                      disabled={verifying || rejecting}
                      data-testid="reject-tenant-btn"
                    >
                      {rejecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                      Reject
                    </Button>
                  </div>
                </div>
              )}

              {/* Profile Details */}
              <div className="flex-1 p-6 space-y-6">
                {/* Personal Info */}
                <section>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Personal Information</h3>
                  <div className="space-y-3">
                    {tenant.birthday && (
                      <div className="flex items-center gap-3 text-sm">
                        <Cake className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground w-32">Date of Birth</span>
                        <span>{formatDate(tenant.birthday)}</span>
                      </div>
                    )}
                    {tenant.school_name && (
                      <div className="flex items-center gap-3 text-sm">
                        <Briefcase className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground w-32">School / University</span>
                        <span>{tenant.school_name}</span>
                      </div>
                    )}
                    {tenant.student_id && (
                      <div className="flex items-center gap-3 text-sm">
                        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground w-32">Student ID</span>
                        <span>{tenant.student_id}</span>
                      </div>
                    )}
                    {tenant.home_address && (
                      <div className="flex items-start gap-3 text-sm">
                        <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                        <span className="text-muted-foreground w-32 shrink-0">Complete Address</span>
                        <span className="break-words">{tenant.home_address}</span>
                      </div>
                    )}
                    {tenant.id_type && (
                      <div className="flex items-center gap-3 text-sm">
                        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground w-32">ID</span>
                        <span>{tenant.id_type}{tenant.id_number ? ` - ${tenant.id_number}` : ''}</span>
                      </div>
                    )}
                    {tenant.notes && (
                      <div className="flex items-start gap-3 text-sm">
                        <FileText className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                        <span className="text-muted-foreground w-32 shrink-0">Notes</span>
                        <span className="break-words">{tenant.notes}</span>
                      </div>
                    )}
                    {!tenant.birthday && !tenant.school_name && !tenant.student_id && !tenant.home_address && !tenant.notes && (
                      <p className="text-sm text-muted-foreground italic">No additional info. Click Edit or send a Tenant Information Form.</p>
                    )}
                  </div>
                </section>

                <Separator />

                {/* Emergency Contact */}
                <section>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    <Heart className="w-4 h-4 inline mr-1" />
                    Emergency Contact
                  </h3>
                  {tenant.emergency_contact?.name ? (
                    <div className="space-y-2 text-sm">
                      <p><strong>{tenant.emergency_contact.name}</strong></p>
                      {tenant.emergency_contact.relationship && (
                        <p className="text-muted-foreground"><span className="font-medium">Relationship:</span> {tenant.emergency_contact.relationship}</p>
                      )}
                      {tenant.emergency_contact.phone && (
                        <p className="text-muted-foreground flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{tenant.emergency_contact.phone}</p>
                      )}
                      {tenant.emergency_contact.address && (
                        <p className="text-muted-foreground flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span className="break-words">{tenant.emergency_contact.address}</span></p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No emergency contact on file.</p>
                  )}
                </section>

                <Separator />

                {/* Lease Info */}
                {tenant.active_lease && (
                  <>
                    <section>
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Active Lease</h3>
                      <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950 space-y-2 text-sm">
                        <p><strong>{getPropertyName(tenant.active_lease.property_id)}</strong> — Room {getUnitName(tenant.active_lease.unit_id)}</p>
                        <p className="text-muted-foreground">
                          {formatDate(tenant.active_lease.start_date)} → {formatDate(tenant.active_lease.end_date)}
                        </p>
                        <p className="font-semibold">{formatCurrency(tenant.active_lease.total_monthly_rent || tenant.active_lease.base_rent)}/mo</p>
                      </div>
                    </section>
                    <Separator />
                  </>
                )}

                {/* Send Invite Section */}
                {(selectedType === 'manual' || selectedType === 'pending' || selectedType === 'available') && (
                  <>
                    <section>
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Send Invite to Use Portal</h3>
                      <Button variant="outline" className="w-full" onClick={() => onInvite(getTenantEmail())} data-testid="send-invite-section-btn">
                        <Send className="w-4 h-4 mr-2" /> Send Invitation to Tenant Portal
                      </Button>
                    </section>
                    <Separator />
                  </>
                )}

                {/* Documents Section */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Documents
                    </h3>
                    <div className="flex gap-2">
                      <input
                        id={`upload-doc-input-${getTenantId()}`}
                        type="file"
                        className="hidden"
                        onChange={handleUploadDocument}
                        data-testid="tenant-document-upload-input"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => document.getElementById(`upload-doc-input-${getTenantId()}`)?.click()}
                        disabled={uploadingDoc}
                        data-testid="upload-tenant-document-btn"
                      >
                        {uploadingDoc ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                        Upload File
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditingDoc(null); setNoteDialogOpen(true); }}
                        data-testid="create-tenant-note-btn"
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        New Note
                      </Button>
                    </div>
                  </div>

                  {/* Resend Lease quick action */}
                  {mostRecentLeaseDoc && (
                    <div className="mb-3 p-2 rounded-lg border border-dashed bg-[#C85A42]/5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="w-full text-[#C85A42] hover:bg-[#C85A42]/10"
                        onClick={handleResendLease}
                        disabled={resendingLease}
                        data-testid="resend-lease-btn"
                      >
                        {resendingLease ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                        Resend Most Recent Lease to Tenant
                      </Button>
                      <p className="text-[11px] text-muted-foreground text-center mt-1.5" data-testid="resend-last-sent">
                        Last sent: {timeAgo(mostRecentLeaseDoc.last_notified_at || mostRecentLeaseDoc.created_at)}
                      </p>
                    </div>
                  )}

                  {tenantDocuments.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No documents yet. Upload any file (PDF, DOCX, image, etc.) or compose a free-form note.</p>
                  ) : (
                    <div className="space-y-2">
                      {tenantDocuments.map(doc => (
                        <div key={doc.doc_id} className="p-3 rounded-lg border text-sm flex items-center justify-between gap-2" data-testid={`document-row-${doc.doc_id}`}>
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {doc.type === 'note' ? (
                              <Pencil className="w-4 h-4 text-violet-500 shrink-0" />
                            ) : (
                              <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                            )}
                            <div className="min-w-0">
                              <p className="font-medium truncate flex items-center gap-1.5" title={doc.name}>
                                {doc.name}
                                {doc.visible_to_tenant === false && (
                                  <span title="Hidden from tenant" data-testid={`document-private-${doc.doc_id}`}>
                                    <EyeOff className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {doc.type === 'note' ? 'Note' : (doc.mime_type || 'File')}
                                {doc.size_bytes != null && ` · ${formatBytes(doc.size_bytes)}`}
                                {doc.created_at && ` · ${formatDate(doc.created_at)}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            {doc.type === 'note' && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => handleEditNote(doc)}
                                data-testid={`edit-document-${doc.doc_id}`}
                                title="Edit Note"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => handleDownloadDocument(doc)}
                              data-testid={`download-document-${doc.doc_id}`}
                              title="Download"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteDocument(doc)}
                              data-testid={`delete-document-${doc.doc_id}`}
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <Separator />

                {/* Registration Link */}
                <section>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    <UserPlus className="w-4 h-4 inline mr-1" />
                    Tenant Registration
                  </h3>
                  <Button size="sm" variant="outline" className="w-full" onClick={handleGetRegistrationLink} data-testid="get-registration-link-btn">
                    <Link2 className="w-3 h-3 mr-2" /> Copy Registration Link
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">Share this link to let tenants sign up directly.</p>
                </section>

                <Separator />

                {/* Danger Zone */}
                <section className="space-y-2">
                  {tenant?.active_lease && (!isManager || can('offboard_tenants')) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-[#C85A42] border-[#C85A42]/40 hover:bg-[#C85A42]/10 w-full"
                      onClick={onOffboard}
                      data-testid="offboard-tenant-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" /> Offboard Tenant
                    </Button>
                  )}
                  {(!isManager || can('invite_tenants')) && (
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 w-full" onClick={onRemove} data-testid="remove-tenant-btn">
                      <Trash2 className="w-4 h-4 mr-2" /> Remove Tenant
                    </Button>
                  )}
                </section>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <TenantNoteDialog
        open={noteDialogOpen}
        onOpenChange={setNoteDialogOpen}
        editingDoc={editingDoc}
        tenantId={getTenantId()}
        onSaved={() => loadTenantDocuments(getTenantId())}
      />
    </>
  );
};
